import { FiatKycPosition } from './FiatKycPosition'
import type { FiatProvider, KYCProvider } from '@funkit/connect-core'
import { Linking, Pressable, ScrollView, StyleSheet } from 'react-native'

import { AttributionWatermark } from '../components/AttributionWatermark'
import { FiatSubmissionIndicator } from './FiatSubmissionIndicator'
import { FiatActionButton } from './FiatActionButton'
import { useFlowStepFrame } from '../hooks/useNestedSheetHeight'
import { KycExternalLinkIcon } from '../icons/KycExternalLinkIcon'
import { KycIdentityIcon } from '../icons/KycIdentityIcon'
import { KycDocumentIcon } from '../icons/KycDocumentIcon'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { FiatBackButton } from './FiatBackButton'
import {
  CONSENT_COPY_VERSION,
  PRIVACY_POLICY_URL,
  kycProviderDisplayName,
} from './legalLinks'

export interface FiatKycIntroScreenProps {
  provider?: FiatProvider
  /** Names the ID-verification vendor in the document round's copy. */
  kycProvider?: KYCProvider
  additionalInformation?: boolean
  documentVerification?: boolean
  isSubmitting?: boolean
  onContinue: () => void
  onCancel: () => void
}

export function FiatKycIntroScreen({
  provider,
  kycProvider,
  additionalInformation = false,
  documentVerification = false,
  isSubmitting = false,
  onContinue,
  onCancel,
}: FiatKycIntroScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height, paddingTop } = useFlowStepFrame()
  const hasConsent = !additionalInformation && !documentVerification
  let title = t('fiat.kycIntroTitle')
  let detail = t('fiat.kycIntroDetail')
  let action = t('fiat.kycAgreeContinue')
  if (documentVerification) {
    const vendor = kycProviderDisplayName(
      kycProvider,
      t('fiat.kycProviderGenericId'),
    )
    title = t('fiat.kycDocumentTitle')
    detail = t('fiat.kycDocumentDetail', { provider: vendor })
    action = t('fiat.kycDocumentContinue', { provider: vendor })
  } else if (additionalInformation) {
    title = t('fiat.kycAdditionalTitle')
    detail = t('fiat.kycAdditionalDetail')
  }
  const continueVerification = () => {
    if (isSubmitting) {
      return
    }
    if (hasConsent) {
      logger.info('connect-rn:fiat:kyc_consent_given', {
        provider,
        consentedAt: new Date().toISOString(),
        copyVersion: CONSENT_COPY_VERSION,
      })
    }
    onContinue()
  }
  const findOutMore = () => {
    void Linking.openURL(PRIVACY_POLICY_URL).catch(() => {
      logger.warn('connect-rn:fiat:kyc_privacy_not_opened')
    })
  }

  return (
    <Box
      style={{ height, paddingTop, backgroundColor: theme.background.modal }}
    >
      <FiatBackButton onPress={onCancel} />
      {documentVerification ? <FiatKycPosition position={3} /> : null}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Box style={[styles.copy, documentVerification && styles.documentCopy]}>
          {documentVerification ? (
            <Box style={{ marginLeft: 2, marginBottom: 14 }}>
              <KycDocumentIcon />
            </Box>
          ) : (
            <KycIdentityIcon color={theme.content.elements.primary} />
          )}
          <Text
            style={[styles.title, { color: theme.content.elements.primary }]}
          >
            {title}
          </Text>
          <Text
            style={[
              styles.detail,
              {
                marginTop: hasConsent ? -3 : 0,
                maxWidth: additionalInformation ? 283 : 268,
                color: theme.content.elements.secondary,
              },
            ]}
          >
            {detail}
          </Text>
          <Pressable
            accessibilityRole="link"
            accessibilityLabel={t('fiat.kycFindOutMore')}
            onPress={findOutMore}
            style={[styles.link, { marginLeft: additionalInformation ? 0 : 2 }]}
          >
            <Text
              weight="medium"
              style={{
                fontSize: 16,
                lineHeight: 18,
                letterSpacing: -0.32,
                color: theme.content.elements.primary,
              }}
            >
              {t('fiat.kycFindOutMore')}
            </Text>
            <KycExternalLinkIcon color={theme.content.elements.primary} />
          </Pressable>
        </Box>
        <Box flex={1} />
        <Box style={styles.actions}>
          {hasConsent ? (
            <Text
              weight="medium"
              style={[
                styles.consent,
                { color: theme.content.elements.secondary },
              ]}
            >
              {t('fiat.kycR2Consent')}
            </Text>
          ) : null}
          <Box style={{ marginLeft: 2, marginRight: -2 }}>
            {isSubmitting ? (
              <FiatSubmissionIndicator />
            ) : (
              <FiatActionButton label={action} onPress={continueVerification} />
            )}
          </Box>
        </Box>
      </ScrollView>
      <AttributionWatermark />
    </Box>
  )
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  copy: { paddingHorizontal: 40, paddingTop: 99, gap: 16 },
  documentCopy: { paddingTop: 100 },
  title: {
    fontWeight: '600',
    fontSize: 40,
    lineHeight: 40,
    letterSpacing: -1.2,
    maxWidth: 314,
  },
  detail: { fontSize: 21, lineHeight: 23.52, letterSpacing: -0.42 },
  link: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    marginTop: -10,
  },
  actions: {
    paddingHorizontal: 24,
    paddingBottom: 73,
    paddingTop: 32,
    gap: 18,
  },
  consent: { paddingHorizontal: 9, fontSize: 12, lineHeight: 13 },
})
